import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Wrench,
  Plus,
  Search,
  Pencil,
  Trash2,
  SlidersHorizontal,
  X,
  ClipboardList,
  History,
} from 'lucide-react';
import { Toast } from '../../components/ui/FormField';
import {
  Field,
  TextInput,
  TextareaInput,
  PrimaryButton as FieldPrimary,
  GhostButton,
} from '../../components/ui/Field';
import { StatsCard } from '../../components/ui/StatsCard';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { ToolForm } from '../../components/forms/ToolForm';
import { useCrudPage } from '../../hooks/useCrudPage';
import { usePageAction } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { useAuthStore } from '../../stores/auth-store';
import { toolApi, toolModelApi, toolServiceRecordApi } from '../../services/api-services';
import type { Tool, ToolModel, ToolServiceRecord } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// InventoryPage — Operations Deck redesign (Variation A)
//
// Layout matches Companies / Locations / Users:
//   • 4-card stats strip (Total / Active / Deployed / Avg hours)
//   • Search + Status segmented + Model filter popover + clear
//   • CSS-grid header row with SortHeader cells (server-side sort)
//   • Tool rows with icon prefix, mono serial, Active dot, mono hours
//   • Pagination footer
//   • Modal Add/Edit (ToolForm) + per-row Service Record / History
//     modals using the Field primitives so typography is consistent
// ─────────────────────────────────────────────────────────────

// Admin-visible column template: Model · Serial · Mfg date · Active · Notes · Hours · Actions
const COL_TEMPLATE =
  'minmax(220px, 2.2fr) minmax(180px, 1.5fr) minmax(110px, 0.9fr) 90px minmax(160px, 1.6fr) 100px 140px';

type StatusFilter = 'all' | 'active' | 'inactive';

export function InventoryPage() {
  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';
  const username = authUser?.username;
  const recordActivity = useActivityStore((s) => s.record);

  const [toolModels, setToolModels] = useState<ToolModel[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [modelFilter, setModelFilter] = useState<number | ''>('');

  const crud = useCrudPage<Tool>({
    fetchFn: toolApi.getAll,
    initialSortCol: 'SerialNumber',
  });
  const [saving, setSaving] = useState(false);

  // Service record state (separate flow from the main edit modal)
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [serviceModal, setServiceModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [serviceForm, setServiceForm] = useState<Partial<ToolServiceRecord>>({
    toolId: 0,
    technicianName: '',
    seviceDate: '',
    serviceWorkPerformed: '',
  });
  const [savingService, setSavingService] = useState(false);
  const [serviceRecords, setServiceRecords] = useState<ToolServiceRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    toolModelApi.getAll({ limit: 1000 }).then((r) => setToolModels(r.entities ?? [])).catch(() => {});
  }, []);

  // ── Filters (client-side pass over the loaded page) ──
  const filteredData = useMemo(() => {
    return crud.data.filter((t) => {
      if (statusFilter === 'active' && !t.active) return false;
      if (statusFilter === 'inactive' && t.active) return false;
      if (modelFilter !== '' && t.toolModelId !== modelFilter) return false;
      return true;
    });
  }, [crud.data, statusFilter, modelFilter]);

  const filtersActive = statusFilter !== 'all' || modelFilter !== '';
  const clearFilters = () => {
    setStatusFilter('all');
    setModelFilter('');
  };

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  // ── Stats — domain summary across the loaded page + DB total ──
  const stats = useMemo(() => {
    const onPage = crud.data;
    const active = onPage.filter((t) => t.active).length;
    const deployed = onPage.filter((t) => t.isUsed).length;
    const totalHours = onPage.reduce((acc, t) => acc + (t.totalHoursDrilling ?? 0), 0);
    const avg = onPage.length > 0 ? totalHours / onPage.length : 0;
    return {
      total: crud.totalCount,
      active,
      deployed,
      avgHours: avg,
    };
  }, [crud.data, crud.totalCount]);

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  // ── CRUD wiring ──
  const handleSave = async (form: Partial<Tool>) => {
    setSaving(true);
    try {
      const label = form.serialNumber?.trim() || 'Untitled tool';
      const res = crud.editingItem ? await toolApi.update(form) : await toolApi.create(form);
      if (res?.status === 'Failed' || res?.error) {
        crud.showToast(res?.error?.errorMessage || 'Failed to save tool', 'error');
        return;
      }
      crud.showToast(crud.editingItem ? 'Tool updated successfully' : 'Tool added to inventory');
      recordActivity({
        type: crud.editingItem ? 'update' : 'create',
        entity: 'Inventory',
        label,
        user: username,
      });
      crud.closeModal();
      crud.load();
    } catch {
      crud.showToast('Failed to save tool', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tool: Tool) => {
    if (!confirm(`Delete tool "${tool.serialNumber}"?`)) return;
    try {
      const res = await toolApi.delete(tool.id);
      if (res?.status === 'Failed' || res?.error) {
        crud.showToast(res?.error?.errorMessage || 'Failed to delete tool', 'error');
        return;
      }
      crud.showToast('Tool deleted');
      recordActivity({
        type: 'delete',
        entity: 'Inventory',
        label: tool.serialNumber?.trim() || `Tool #${tool.id}`,
        user: username,
      });
      crud.load();
    } catch {
      crud.showToast('Failed to delete tool', 'error');
    }
  };

  const openServiceRecord = (tool: Tool) => {
    setSelectedTool(tool);
    setServiceForm({
      toolId: tool.id,
      technicianName: '',
      seviceDate: '',
      serviceWorkPerformed: '',
    });
    setServiceModal(true);
  };

  const handleSaveServiceRecord = async () => {
    if (!serviceForm.technicianName?.trim() || !serviceForm.serviceWorkPerformed?.trim()) {
      crud.showToast('Technician and work performed are required', 'error');
      return;
    }
    setSavingService(true);
    try {
      await toolServiceRecordApi.create(serviceForm);
      crud.showToast('Service record created');
      setServiceModal(false);
    } catch {
      crud.showToast('Failed to save service record', 'error');
    } finally {
      setSavingService(false);
    }
  };

  const loadServiceHistory = async (tool: Tool) => {
    setSelectedTool(tool);
    setHistoryModal(true);
    setLoadingHistory(true);
    try {
      const r = await toolServiceRecordApi.getAllByToolId(tool.id, { limit: 200 });
      setServiceRecords(r.entities ?? []);
    } catch {
      setServiceRecords([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ── TopBar primary action ──
  usePageAction(
    isSuperAdmin ? (
      <FieldPrimary onClick={crud.openCreate}>
        <Plus size={14} /> Add tool
      </FieldPrimary>
    ) : null,
    [isSuperAdmin],
  );

  const activeFilterCount = (statusFilter !== 'all' ? 1 : 0) + (modelFilter !== '' ? 1 : 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Stats strip */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatsCard
          label="Total tools"
          value={stats.total}
          sub="In active inventory"
          accent="var(--color-accent)"
          loading={crud.loading}
        />
        <StatsCard
          label="Active (this page)"
          value={stats.active}
          sub="Available for service"
          accent="var(--color-teal)"
          loading={crud.loading}
        />
        <StatsCard
          label="Currently deployed"
          value={stats.deployed}
          sub="Checked into a well"
          accent="#7c6ba8"
          loading={crud.loading}
        />
        <StatsCard
          label="Avg hours / tool"
          value={Number.isFinite(stats.avgHours) ? stats.avgHours.toFixed(1) : '0.0'}
          sub="Cumulative downhole hours"
          accent="var(--color-warn)"
          loading={crud.loading}
        />
      </div>

      {/* Filter / search bar + grid */}
      <div
        style={{
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border)',
            flexWrap: 'wrap',
          }}
        >
          {/* Search */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              background: 'var(--color-panel-alt)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              width: 280,
              transition: 'border-color 180ms',
            }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search tools, S/N, model…"
              value={crud.searchText}
              onChange={(e) => {
                crud.setSearchText(e.target.value);
                crud.setOffset(0);
              }}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13.5,
                flex: 1,
                fontFamily: 'var(--font-body)',
                color: 'var(--color-text)',
                width: '100%',
              }}
            />
            {crud.searchText && (
              <button
                onClick={() => { crud.setSearchText(''); crud.setOffset(0); }}
                aria-label="Clear search"
                title="Clear search"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 18, height: 18, borderRadius: 9, padding: 0,
                  background: 'transparent', border: 'none',
                  color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(15,23,32,0.08)';
                  e.currentTarget.style.color = 'var(--color-text)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-dim)';
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Status segmented */}
          <StatusSegmented value={statusFilter} onChange={setStatusFilter} />

          {/* Model filter popover */}
          <FilterPopover
            toolModels={toolModels}
            modelFilter={modelFilter}
            onChangeModel={setModelFilter}
            onClear={clearFilters}
            activeCount={activeFilterCount}
          />

          {filtersActive && (
            <button
              onClick={clearFilters}
              style={{
                fontSize: 12,
                color: 'var(--color-accent)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '6px 8px',
                borderRadius: 5,
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(11,74,111,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Clear all
            </button>
          )}

          <div style={{ flex: 1 }} />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading
              ? 'Loading…'
              : filtersActive
                ? `${filteredData.length} of ${crud.data.length} on page · ${crud.totalCount} total`
                : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COL_TEMPLATE,
            padding: '12px 18px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-panel-alt)',
            fontSize: 12.5,
            letterSpacing: 0.3,
            color: 'var(--color-text-mid)',
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
          }}
        >
          <SortHeader label="Model" col="ToolModelName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Serial number" col="SerialNumber" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Mfg date" col="ManufacturerDate" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Active" col="Active" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Notes" col="Note" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <div style={{ paddingRight: 18 }}>
            <SortHeader label="Total hrs" col="TotalHoursDrilling" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} align="right" />
          </div>
          <div style={{ textAlign: 'right' }}>Actions</div>
        </div>

        {/* Rows */}
        {crud.loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>
            Loading…
          </div>
        ) : filteredData.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
            <Wrench size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <div style={{ fontSize: 14, marginBottom: 4 }}>
              {filtersActive ? 'No tools match these filters' : 'No tools in inventory yet'}
            </div>
            <div style={{ fontSize: 12 }}>
              {filtersActive
                ? 'Try clearing filters or moving to another page.'
                : isSuperAdmin
                  ? 'Add a tool to get started.'
                  : 'Tools registered for your company will appear here.'}
            </div>
          </div>
        ) : (
          <div>
            {filteredData.map((t, i) => (
              <ToolRow
                key={t.id ?? i}
                tool={t}
                isLast={i === filteredData.length - 1}
                canManage={!!isSuperAdmin}
                onEdit={() => crud.openEdit(t)}
                onDelete={() => handleDelete(t)}
                onAddService={() => openServiceRecord(t)}
                onHistory={() => loadServiceHistory(t)}
              />
            ))}
          </div>
        )}

        {/* Pagination footer */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            color: 'var(--color-text-dim)',
            background: 'var(--color-panel-alt)',
          }}
        >
          <div>
            Showing{' '}
            <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{filteredData.length}</span>
            {filtersActive && filteredData.length !== crud.data.length && (
              <> (of {crud.data.length} on page)</>
            )}{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span> tool
            {crud.totalCount === 1 ? '' : 's'}
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)' }}>
              <PaginationButton
                onClick={() => crud.setOffset(Math.max(0, crud.offset - crud.pageSize))}
                disabled={crud.offset === 0}
              >
                ←
              </PaginationButton>
              <span style={{ padding: '0 10px', color: 'var(--color-text-mid)' }}>
                {currentPage} / {totalPages}
              </span>
              <PaginationButton
                onClick={() => crud.setOffset(crud.offset + crud.pageSize)}
                disabled={currentPage >= totalPages}
              >
                →
              </PaginationButton>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Tool modal */}
      <ToolForm
        open={crud.modalOpen}
        editing={crud.editingItem}
        saving={saving}
        toolModels={toolModels}
        onCancel={crud.closeModal}
        onSave={handleSave}
      />

      {/* Service record modal (create new) */}
      <ServiceRecordModal
        open={serviceModal}
        tool={selectedTool}
        form={serviceForm}
        saving={savingService}
        onChange={setServiceForm}
        onCancel={() => setServiceModal(false)}
        onSave={handleSaveServiceRecord}
      />

      {/* Service record history modal (read-only list) */}
      <ServiceHistoryModal
        open={historyModal}
        tool={selectedTool}
        loading={loadingHistory}
        records={serviceRecords}
        onClose={() => setHistoryModal(false)}
      />

      {crud.toast && (
        <Toast
          message={crud.toast.message}
          type={crud.toast.type}
          onClose={() => crud.setToast(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Tool row
// ─────────────────────────────────────────────────────────────

function ToolRow({
  tool: t,
  isLast,
  canManage,
  onEdit,
  onDelete,
  onAddService,
  onHistory,
}: {
  tool: Tool;
  isLast: boolean;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onAddService: () => void;
  onHistory: () => void;
}) {
  const [hover, setHover] = useState(false);
  const modelName = t.toolModel?.toolModelName ?? '—';
  const hours = t.totalHoursDrilling ?? 0;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: COL_TEMPLATE,
        padding: '13px 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'center',
        fontSize: 14,
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        transition: 'background 120ms',
      }}
    >
      {/* Model */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            background: 'rgba(11,74,111,0.10)',
            color: 'var(--color-accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Wrench size={14} />
        </div>
        <div
          style={{
            color: 'var(--color-text)',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={modelName}
        >
          {modelName}
        </div>
      </div>

      {/* Serial */}
      <Cell mono accent ellipsis title={t.serialNumber ?? ''}>
        {t.serialNumber || '—'}
      </Cell>

      {/* Mfg date */}
      <Cell mono dim={!t.manufacturerDate}>
        {t.manufacturerDate ? formatDate(t.manufacturerDate) : '—'}
      </Cell>

      {/* Active */}
      <div>
        {t.active ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11.5,
              color: 'var(--color-teal)',
              fontWeight: 500,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--color-teal)' }} />
            Active
          </span>
        ) : (
          <span style={{ color: 'var(--color-text-dim)', fontSize: 11.5 }}>Inactive</span>
        )}
      </div>

      {/* Notes */}
      <Cell dim={!t.note} ellipsis title={t.note ?? ''}>
        {t.note || '—'}
      </Cell>

      {/* Total hours */}
      <div
        style={{
          textAlign: 'right',
          paddingRight: 18,
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          fontWeight: 500,
          color: hours > 0 ? 'var(--color-text)' : 'var(--color-text-dim)',
        }}
      >
        {hours.toFixed(1)}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <IconButton title="Service record history" onClick={onHistory}>
          <History size={15} />
        </IconButton>
        {canManage && (
          <>
            <IconButton title="Add service record" onClick={onAddService}>
              <ClipboardList size={15} />
            </IconButton>
            <IconButton title="Edit" onClick={onEdit}>
              <Pencil size={15} />
            </IconButton>
            <IconButton title="Delete" onClick={onDelete} hoverDanger>
              <Trash2 size={15} />
            </IconButton>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Status segmented + Filter popover
// ─────────────────────────────────────────────────────────────

function StatusSegmented({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}) {
  const opts: { v: StatusFilter; label: string; dot?: string }[] = [
    { v: 'all', label: 'All' },
    { v: 'active', label: 'Active', dot: 'var(--color-teal)' },
    { v: 'inactive', label: 'Inactive', dot: 'var(--color-text-dim)' },
  ];
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        background: 'var(--color-panel-alt)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        gap: 2,
      }}
    >
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px',
              border: 'none',
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: 'var(--font-body)',
              cursor: 'pointer',
              background: active ? 'var(--color-panel)' : 'transparent',
              color: active ? 'var(--color-text)' : 'var(--color-text-mid)',
              boxShadow: active ? '0 1px 2px rgba(15,23,32,0.06)' : 'none',
              transition: 'all 120ms',
            }}
          >
            {o.dot && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: o.dot,
                  display: 'inline-block',
                }}
              />
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function FilterPopover({
  toolModels,
  modelFilter,
  onChangeModel,
  onClear,
  activeCount,
}: {
  toolModels: ToolModel[];
  modelFilter: number | '';
  onChangeModel: (v: number | '') => void;
  onClear: () => void;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const POP_WIDTH = 340;

  const updateCoords = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const top = rect.bottom + 8;
    const desiredLeft = rect.left;
    const maxLeft = window.innerWidth - POP_WIDTH - 12;
    const left = Math.max(12, Math.min(desiredLeft, maxLeft));
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => updateCoords();
    const onResize = () => updateCoords();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updateCoords]);

  const isActive = activeCount > 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 12px',
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'var(--font-body)',
          cursor: 'pointer',
          background: open ? 'var(--color-panel-alt)' : 'var(--color-panel)',
          color: isActive ? 'var(--color-accent)' : 'var(--color-text-mid)',
          border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`,
          borderRadius: 7,
          transition: 'all 120ms',
        }}
      >
        <SlidersHorizontal size={14} />
        Filter
        {isActive && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: 'var(--color-accent)',
              color: '#fff',
              fontSize: 10.5,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}
          >
            {activeCount}
          </span>
        )}
      </button>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: POP_WIDTH,
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(15,23,32,0.12)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                color: 'var(--color-text-dim)',
                fontFamily: 'var(--font-mono)',
                fontWeight: 500,
              }}
            >
              Filter by
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-text-dim)',
                borderRadius: 4,
              }}
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <PopoverField label="Tool model">
              <FilterSelect
                value={modelFilter === '' ? '' : String(modelFilter)}
                onChange={(v) => onChangeModel(v === '' ? '' : Number(v))}
                placeholder="Any model"
                options={toolModels.map((m) => ({ value: String(m.id), label: m.toolModelName }))}
              />
            </PopoverField>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 16px',
              borderTop: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
            }}
          >
            <button
              onClick={onClear}
              disabled={activeCount === 0}
              style={{
                fontSize: 12,
                color: activeCount === 0 ? 'var(--color-text-dim)' : 'var(--color-accent)',
                background: 'transparent',
                border: 'none',
                cursor: activeCount === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
                padding: '4px 4px',
              }}
            >
              Clear filters
            </button>
            <button
              onClick={() => setOpen(false)}
              style={{
                fontSize: 12.5,
                fontWeight: 500,
                background: 'var(--color-accent)',
                color: '#fff',
                border: 'none',
                padding: '6px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}
            >
              Apply
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function PopoverField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span
        style={{
          fontSize: 11.5,
          color: 'var(--color-text-mid)',
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}) {
  const hasValue = !!value;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%',
        height: 34,
        padding: '0 30px 0 11px',
        fontSize: 13,
        fontFamily: 'var(--font-body)',
        color: hasValue ? 'var(--color-text)' : 'var(--color-text-mid)',
        background: `var(--color-panel) url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1l4 4 4-4' stroke='%238894a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat right 11px center`,
        border: `1px solid ${hasValue ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 7,
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        outline: 'none',
        cursor: 'pointer',
        fontWeight: hasValue ? 500 : 400,
        transition: 'border-color 120ms',
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────
// Service Record + History modals
// ─────────────────────────────────────────────────────────────

function ServiceRecordModal({
  open,
  tool,
  form,
  saving,
  onChange,
  onCancel,
  onSave,
}: {
  open: boolean;
  tool: Tool | null;
  form: Partial<ToolServiceRecord>;
  saving: boolean;
  onChange: (next: Partial<ToolServiceRecord>) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, saving, onCancel]);

  if (!open || !tool) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(15,23,32,0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '8vh 16px 5vh',
        overflowY: 'auto',
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 560,
          background: 'var(--color-panel)',
          borderRadius: 14,
          boxShadow: '0 32px 80px rgba(0,0,0,0.30)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '20px 24px 16px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
            <div
              style={{
                width: 38, height: 38, borderRadius: 9,
                background: 'rgba(11,74,111,0.10)',
                color: 'var(--color-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginTop: 2,
              }}
            >
              <ClipboardList size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.4,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                }}
              >
                Service record · New entry
              </div>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 22,
                  fontWeight: 500,
                  letterSpacing: -0.3,
                  color: 'var(--color-text)',
                  marginTop: 3,
                  marginBottom: 4,
                  lineHeight: 1.2,
                }}
              >
                Log service for {tool.serialNumber}
              </h2>
              <p style={{ fontSize: 12.5, color: 'var(--color-text-mid)', margin: 0, lineHeight: 1.45 }}>
                Records a technician's work against this tool's history.
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            aria-label="Close"
            disabled={saving}
            style={{
              width: 30, height: 30, borderRadius: 6,
              background: 'transparent', border: 'none',
              color: 'var(--color-text-dim)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <X size={18} />
          </button>
        </header>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Technician name" required>
              <TextInput
                value={form.technicianName ?? ''}
                placeholder="Jane Doe"
                onChange={(e) => onChange({ ...form, technicianName: e.target.value })}
              />
            </Field>
            <Field label="Service date">
              <TextInput
                type="date"
                value={form.seviceDate ?? ''}
                style={{ fontFamily: 'var(--font-mono)' }}
                onChange={(e) => onChange({ ...form, seviceDate: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Service work performed" required>
            <TextareaInput
              rows={5}
              value={form.serviceWorkPerformed ?? ''}
              placeholder="Re-calibrated sensor; replaced O-rings; bench-tested under nominal load…"
              onChange={(e) => onChange({ ...form, serviceWorkPerformed: e.target.value })}
            />
          </Field>
        </div>

        <footer
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '14px 24px',
            background: 'var(--color-panel-alt)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <GhostButton onClick={onCancel} disabled={saving}>
            Cancel
          </GhostButton>
          <FieldPrimary
            onClick={onSave}
            disabled={saving || !form.technicianName?.trim() || !form.serviceWorkPerformed?.trim()}
          >
            {saving ? 'Saving…' : 'Save record'}
          </FieldPrimary>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function ServiceHistoryModal({
  open,
  tool,
  loading,
  records,
  onClose,
}: {
  open: boolean;
  tool: Tool | null;
  loading: boolean;
  records: ToolServiceRecord[];
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !tool) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(15,23,32,0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '6vh 16px 5vh',
        overflowY: 'auto',
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 760,
          background: 'var(--color-panel)',
          borderRadius: 14,
          boxShadow: '0 32px 80px rgba(0,0,0,0.30)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '85vh',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '20px 24px 16px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
            <div
              style={{
                width: 38, height: 38, borderRadius: 9,
                background: 'rgba(11,74,111,0.10)',
                color: 'var(--color-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginTop: 2,
              }}
            >
              <History size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.4,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                }}
              >
                Tool history · {records.length} record{records.length === 1 ? '' : 's'}
              </div>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 22,
                  fontWeight: 500,
                  letterSpacing: -0.3,
                  color: 'var(--color-text)',
                  marginTop: 3,
                  marginBottom: 4,
                  lineHeight: 1.2,
                }}
              >
                Service log — {tool.serialNumber}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30, height: 30, borderRadius: 6,
              background: 'transparent', border: 'none',
              color: 'var(--color-text-dim)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <X size={18} />
          </button>
        </header>

        <div style={{ padding: '14px 24px 20px', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>
              Loading…
            </div>
          ) : records.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--color-text-dim)' }}>
              <History size={28} style={{ margin: '0 auto 10px', opacity: 0.4 }} />
              <div style={{ fontSize: 13.5 }}>No service records yet.</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Use “Add service record” to log the first entry.</div>
            </div>
          ) : (
            <div
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(160px,1.2fr) 130px minmax(220px,2.2fr)',
                  padding: '10px 14px',
                  background: 'var(--color-panel-alt)',
                  borderBottom: '1px solid var(--color-border)',
                  fontSize: 11.5,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: 'var(--color-text-mid)',
                  fontWeight: 600,
                  fontFamily: 'var(--font-body)',
                }}
              >
                <div>Technician</div>
                <div>Date</div>
                <div>Work performed</div>
              </div>
              {records.map((rec, i) => (
                <div
                  key={rec.id ?? i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(160px,1.2fr) 130px minmax(220px,2.2fr)',
                    padding: '12px 14px',
                    borderBottom: i === records.length - 1 ? 'none' : '1px solid var(--color-border)',
                    alignItems: 'start',
                    fontSize: 13.5,
                  }}
                >
                  <div style={{ fontWeight: 500, color: 'var(--color-text)' }}>{rec.technicianName || '—'}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--color-text-mid)' }}>
                    {rec.seviceDate ? formatDate(rec.seviceDate) : '—'}
                  </div>
                  <div style={{ color: 'var(--color-text-mid)', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                    {rec.serviceWorkPerformed || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <footer
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '14px 24px',
            background: 'var(--color-panel-alt)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <GhostButton onClick={onClose}>Close</GhostButton>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────
// Small cell + button helpers (mirror SitesPage)
// ─────────────────────────────────────────────────────────────

function Cell({
  children,
  mono = false,
  dim = false,
  accent = false,
  ellipsis = false,
  style,
  title,
}: {
  children: React.ReactNode;
  mono?: boolean;
  dim?: boolean;
  accent?: boolean;
  ellipsis?: boolean;
  style?: CSSProperties;
  title?: string;
}) {
  return (
    <div
      title={title}
      style={{
        fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
        color: accent
          ? 'var(--color-accent)'
          : dim
            ? 'var(--color-text-mid)'
            : 'var(--color-text)',
        fontSize: mono ? 13 : 14,
        whiteSpace: ellipsis ? 'nowrap' : undefined,
        overflow: ellipsis ? 'hidden' : undefined,
        textOverflow: ellipsis ? 'ellipsis' : undefined,
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function IconButton({
  children,
  onClick,
  title,
  hoverDanger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  hoverDanger?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const idleBg = 'var(--color-panel)';
  const idleBorder = 'var(--color-border)';
  const idleColor = hoverDanger ? '#a14a47' : 'var(--color-accent)';
  const hoverBg = hoverDanger ? 'rgba(185,28,28,0.08)' : 'rgba(11,74,111,0.08)';
  const hoverBorder = hoverDanger ? 'rgba(185,28,28,0.35)' : 'var(--color-accent)';
  const hoverColor = hoverDanger ? 'var(--color-bad)' : 'var(--color-accent-hover)';
  const active = hover;
  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 7,
        border: `1px solid ${active ? hoverBorder : idleBorder}`,
        cursor: 'pointer',
        background: active ? hoverBg : idleBg,
        color: active ? hoverColor : idleColor,
        transition: 'all 120ms',
        transform: active ? 'translateY(-1px)' : 'none',
        boxShadow: active ? '0 2px 6px rgba(15,23,32,0.08)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function PaginationButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 26,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--color-border)',
        background: 'var(--color-panel)',
        color: disabled ? 'var(--color-text-dim)' : 'var(--color-text-mid)',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

// ─── helpers ───────────────────────────────────────────────
function formatDate(d?: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return d;
  }
}
